import React from "react";
import { Braces, MessageSquare } from "lucide-react";
import { toast } from "sonner";

import { useCheckInterface, usePatchInterface, useSimulateInterface, type useInterfaces } from "@/shared/api/interfaces";
import { GhostButton, RowList, StatusBadge } from "@/shared/components/settings-atoms";
import { catalogFor, interfaceName } from "@/shared/lib/interface-catalog";
import { InterfaceRow } from "./InterfaceRow";

import type { LucideIcon } from "lucide-react";
import type { Agent, Interface, SystemInterface } from "@/shared/types";
import type { OpenDialog } from "../types";

/** What "Simulate incoming message" sends: a first message that makes the agent introduce itself. */
const TEST_MESSAGE = "Hi! What can you help me with?";

const note = "text-[12.5px] text-[var(--text-quaternary)] py-3";

/** The ways in every agent has: nobody connects them, so there is nothing to set up here. */
const SYSTEM: { type: SystemInterface; Icon: LucideIcon; desc: string }[] = [
  { type: "platform", Icon: MessageSquare, desc: "Chats in this app, with your workspace's members." },
  { type: "api", Icon: Braces, desc: "Runs started with an API key for this agent." },
];

/** A system interface: always on, read only. */
function SystemRow({ type, Icon, desc }: (typeof SYSTEM)[number]) {
  return (
    <div role="group" aria-label={interfaceName(type)} className="flex items-center gap-x-3 py-3.5">
      <div className="w-9 h-9 shrink-0 rounded-[10px] bg-[var(--surface-2)] flex items-center justify-center">
        <Icon className="w-[18px] h-[18px] text-[var(--text-secondary)]" strokeWidth={1.75} />
      </div>
      <div className="flex-1 basis-0 min-w-0">
        <div className="flex items-center gap-2 min-w-0">
          <span className="min-w-0 truncate text-[13.5px] font-medium text-[var(--text-primary)]">{interfaceName(type)}</span>
          <StatusBadge>Built in</StatusBadge>
        </div>
        <div className="text-[12.5px] text-[var(--text-tertiary)] mt-[1px]">{desc}</div>
      </div>
    </div>
  );
}

/* The Connected group's body: the agent's system interfaces, which are always
   there, then its channels in whichever state the list is in, and what each
   row's own controls do. Setup and disconnect are dialogs, so they go up to the
   page; the rest are one request each. */
export function ConnectedInterfaces({ agent, list, onOpen, onOpenTraces, onSimulated, onViewChats }: {
  agent: Agent;
  list: ReturnType<typeof useInterfaces>;
  onOpen: (dialog: OpenDialog) => void;
  onOpenTraces?: () => void;
  onSimulated?: (iface: Interface, threadId: string) => void;
  onViewChats?: (iface: Interface) => void;
}) {
  const patch = usePatchInterface();
  const check = useCheckInterface();
  const simulate = useSimulateInterface();

  const system = SYSTEM.map((entry) => <SystemRow key={entry.type} {...entry} />);
  if (list.isPending) return <RowList>{system}<p className={`m-0 ${note}`}>Loading channels…</p></RowList>;
  if (list.error) {
    return (
      <RowList>
        {system}
        <div className="flex items-center gap-3 py-3">
          <p className="m-0 flex-1 text-[12.5px] text-[var(--danger)]">Couldn't load channels — {list.error.message}</p>
          <GhostButton onClick={() => list.refetch()}>Try again</GhostButton>
        </div>
      </RowList>
    );
  }
  if (!list.data.length) {
    return (
      <RowList>
        {system}
        <p className={`m-0 ${note}`}>No channels yet — connect one below to put {agent.name} where your people already chat.</p>
      </RowList>
    );
  }

  const nameOf = (iface: Interface) => iface.label ?? catalogFor(iface.type)?.name ?? iface.type;

  const toggle = (iface: Interface) => {
    const goingLive = iface.status !== "live";
    patch.mutate(
      { id: iface.id, body: { status: goingLive ? "live" : "paused" } },
      {
        onSuccess: () =>
          toast.success(goingLive ? `${nameOf(iface)} is live again` : `${nameOf(iface)} paused`, {
            description: goingLive ? undefined : "Messages that arrive while it is paused get no answer.",
          }),
      },
    );
  };

  const toggleGroups = (iface: Interface) => {
    const allow = !iface.allow_groups;
    patch.mutate(
      { id: iface.id, body: { allow_groups: allow } },
      {
        onSuccess: () =>
          toast.success(allow ? `${nameOf(iface)} answers in groups again` : `${nameOf(iface)} stays out of groups`, {
            description: allow ? undefined : "Group messages get no answer; one-to-one chats still do.",
          }),
      },
    );
  };

  const test = (iface: Interface) =>
    check.mutate(iface.id, {
      onSuccess: (fresh) =>
        fresh.connection === "ready"
          ? toast.success("Connected", { description: `${nameOf(fresh)} is answering for ${agent.name}.` })
          : toast.error(fresh.error ?? "Setup isn't finished yet."),
    });

  const sendTest = (iface: Interface) =>
    simulate.mutate(
      { id: iface.id, body: { text: TEST_MESSAGE } },
      { onSuccess: ({ thread_id }) => onSimulated?.(iface, thread_id) },
    );

  return (
    <RowList>
      {system}
      {list.data.map((iface) => (
        <InterfaceRow
          key={iface.id}
          iface={iface}
          onToggle={() => toggle(iface)}
          onToggleGroups={() => toggleGroups(iface)}
          onBotChannels={() => onOpen({ dialog: "channels", iface })}
          onRepair={() => onOpen({ dialog: "setup", type: iface.type, existing: iface })}
          onTest={() => test(iface)}
          onSimulate={() => sendTest(iface)}
          onViewChats={() => onViewChats?.(iface)}
          onOpenTraces={() => onOpenTraces?.()}
          onDisconnect={() => onOpen({ dialog: "disconnect", iface })}
        />
      ))}
    </RowList>
  );
}
